import { useCallback } from 'react';
import { useTaskAudioPlayer } from 'students/views/shared/bundles/audio/hooks';

import {
  AudioTags,
  tTaskAudioManagerItems
} from 'students/views/shared/bundles/audio/models';
import startSound from '../assets/game_begins.mp3';
import finishSound from '../assets/game_finish.mp3';
import correctSound from '../assets/match_correct.mp3';
import incorrectSound from '../assets/match_incorrect.mp3';

/**
 * Namespaced per surface: Play ships its own `match_correct`/`match_incorrect`
 * files, which the bare ids used to share — and lose — with the games.
 */
export const PlayAudioIds = {
  correct: 'play:correct',
  incorrect: 'play:incorrect',
  start: 'play:start',
  finish: 'play:finish'
} as const;

const audioItems: tTaskAudioManagerItems = [
  {
    id: PlayAudioIds.correct,
    tags: [AudioTags.SoundEffects],
    src: correctSound,
    html5: false,
    preload: true,
    caching: true,
    volume: 0.1
  },
  {
    id: PlayAudioIds.incorrect,
    tags: [AudioTags.SoundEffects],
    src: incorrectSound,
    html5: false,
    preload: true,
    caching: true,
    volume: 0.1
  },
  {
    id: PlayAudioIds.start,
    tags: [AudioTags.SoundEffects],
    src: startSound,
    html5: false,
    preload: true,
    caching: true,
    volume: 0.1
  },
  {
    id: PlayAudioIds.finish,
    tags: [AudioTags.SoundEffects],
    src: finishSound,
    html5: false,
    preload: true,
    caching: true,
    volume: 0.1
  }
];

interface IReturnedObject {
  playIncorrectSound: () => void;
  playCorrectSound: () => void;
  playStartSound: () => void;
  playFinishSound: () => void;
  playSound: (id: string | number) => void;
}

export default function usePlayAudioPlayer(): IReturnedObject {
  const { playById } = useTaskAudioPlayer(audioItems);

  const playSound = useCallback(
    (id: string | number) => {
      playById(id);
    },
    [playById]
  );

  const playCorrectSound = useCallback(() => {
    playSound(PlayAudioIds.correct);
  }, [playSound]);

  const playIncorrectSound = useCallback(() => {
    playSound(PlayAudioIds.incorrect);
  }, [playSound]);

  const playStartSound = useCallback(() => {
    playSound(PlayAudioIds.start);
  }, [playSound]);

  const playFinishSound = useCallback(() => {
    playSound(PlayAudioIds.finish);
  }, [playSound]);

  return {
    playCorrectSound,
    playIncorrectSound,
    playStartSound,
    playFinishSound,
    playSound
  };
}
